import type { SearchProducts, PurchaseProduct, GenerateProduct, POI} from '@white-label-types/parking-booking';
import type { Cost, CartItem } from '@white-label-types/parking-checkout';
import type { GtmInjected as GTM } from '@white-label-types/nuxtjs-gtm';
import type { TranslateResult } from 'vue-i18n';

// Helpers
import {
  gaDataLayer,
  buildSearchProductGtmData,
  buildCartItemsGtmData,
  buildReceiptGtmData,
  buildInsuranceGtmData,
  buildInsuranceGaDataLayer,
  gaDataLayerCommon,
  getProductGaData,
} from '@white-label-helper/ga-data-layer';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { formatPrice } from '@white-label-helper/helper-payment';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import { datadogRum } from '@datadog/browser-rum';
import type { TravelInsuranceForm } from '@white-label-store/travel-insurance'
import type { LoungeFormData } from '@white-label-types/component';

import { FEATURE_FLAGS, ATTACHMENT_TYPE, EXTRAS_PRODUCT_CODES } from '@white-label-configuration/constants';

type TrackingProduct = {
  item_id: string,
  item_name: string,
  coupon: string,
  currency: string,
  discount: number,
  item_list_position: number,
  item_category: string,
  item_list_name: string,
  price: string | undefined,
  quantity: number,
  upgraded: boolean,
};

/**
 * creates and pushes a complete dataLayer object for product interactions.
 * this is used on pages like travel-extras
 * @param gtm - GTM instance.
 * @param searchCriteria - main search form data - searchBoxFormData
 * @param product - specific product to be actioned on
 * @param primaryProductType - primary product code like 'parking', 'fasttrack,' 'lounges'.
 * @param insuranceFormData - optional form data from travel insurance store
 */

export function trackProductInteraction({
  gtm,
  searchCriteria,
  product,
  primaryProductType,
  insuranceFormData,
}: {
  gtm: GTM;
  searchCriteria: LoungeFormData;
  product: SearchProducts;
  primaryProductType: string;
  insuranceFormData?: TravelInsuranceForm | null;
}): void {
  const finalGtmData = buildSearchProductGtmData(
    searchCriteria,
    product,
    primaryProductType,
    insuranceFormData,
  );

  gtm.push(gaDataLayer(finalGtmData));
}

/**
 * creates and pushes a complete dataLayer object for payment page load
 * this is used on payment page
 */
export function trackPaymentPageLoad({
  gtm,
  cartItems,
  primaryProductType,
  poi,
  authData,
}: {
  gtm: GTM;
  cartItems: CartItem[];
  primaryProductType: string;
  poi: { code?: string; name?: string };
  authData: { isAuthenticated: boolean; userId?: string };
}): void {
  const finalGtmData = buildCartItemsGtmData(
    cartItems,
    primaryProductType,
    poi,
    authData
  );

  if (Object.keys(finalGtmData).length > 0) {
    //in standalone insurance pushes directly to avoid gaDataLayer()
    if (primaryProductType === 'travel_insurance') {
      pushToGTM(gtm, finalGtmData);
    } else {
      gtm.push(gaDataLayer(finalGtmData));
    }
  }
}

/**
 * creates and pushes a complete dataLayer object for the receipt page load.
 * this is used on receipt page
 */
export function trackReceiptPageLoad({
  gtm,
  items,
  poi,
  authData,
  orderReference,
}: {
  gtm: GTM;
  items: PurchaseProduct[];
  poi: POI;
  authData: { isAuthenticated: boolean; userId?: string };
  orderReference: string;
}): void {
  const finalGtmData = buildReceiptGtmData(
    items,
    poi,
    authData,
    orderReference
  );

  if (Object.keys(finalGtmData).length > 0) {
    const primaryItem = items.find(item => {
      const criteria = item.order_criteria[item.product_code as keyof typeof item.order_criteria];
      return !!(criteria && criteria.is_primary);
    }) || items[0];

    //in standalone insurance pushes directly to avoid gaDataLayer()
    if (primaryItem?.product_code === 'travel_insurance') {
      pushToGTM(gtm, finalGtmData);
    } else {
      gtm.push(gaDataLayer(finalGtmData));
    }
  }
}

/**
 * Generates a SHA-256 hash of the input string.
 * @param input - The input string to hash.
 * @returns A promise that resolves to the SHA-256 hash of the input string in hexadecimal format.
 * @throws Error if the crypto API is not supported or other issues occur during hashing.
 */
async function createHash(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input.trim().toLowerCase());
  const hash = await window.crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hash));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Gets the price without the currency symbol */
const getPrice = (price: number) => formatPrice(price).toString().match(/[\d.,]/g)?.join('');

const getPaymentType = (items: PurchaseProduct[]) => {
  const isPaypal = items.find(item => item.payment.payment_type?.toLowerCase().includes('paypal'));
  const isApple = items.find(item => item.payment.payment_type?.toLowerCase().includes('apple_pay'));
  const isGoogle = items.find(item => item.payment.payment_type?.toLowerCase().includes('google_pay'));

  if (isPaypal) {
    return 'PayPal';
  }

  if (isApple) {
    return 'Apple';
  }

  if (isGoogle) {
    return 'Google';
  }

  return 'Card';
};

const getCartItemListName = (item: GenerateProduct) => {
  let itemListName;
  switch(item.attachment_type) {
    case 'primary':
    case 'primary_bundle':
      itemListName = 'search_results';
      break;
    case 'cross_sell':
      itemListName = 'extras';
      break;
    case 'modify':
      itemListName = 'amend';
      break;
    default:
      itemListName = item?.meta?.filter?.item_list_name;
  }
  return itemListName;
}

export const convertCartItems = (
    items: GenerateProduct[],
    actionType = 'add'
  ): TrackingProduct[] => items.map((item: GenerateProduct, index) => {
  const noValue = actionType === 'add' ? undefined : '';
  const productCode = item?.product_code || item?.item_category;

  //handle travel insurance with sub_options
  if (productCode === EXTRAS_PRODUCT_CODES.TRAVEL_INSURANCE || productCode === 'travel_insurance') {
    //calculate total price from selected sub_options
    const subOptions = item.inventory_option?.sub_options || [];
    const totalPrice = subOptions.reduce((sum, opt) => {
      const quantity = opt.totals?.quantity || 0;
      if (quantity > 0) {
        return sum + (opt.totals?.total || 0);
      }
      return sum;
    }, 0);

    return {
      item_id: item?.inventory_id,
      item_name: 'Travel Insurance',
      coupon: item?.discount && item?.discount?.code ? item.discount.code : null,
      currency: getAppVariable('default_currency'),
      discount: item?.totals?.discount || 0,
      item_brand: item.inventory_provider || noValue,
      item_variant: noValue,
      item_list_id: index,
      item_category: 'Insurance',
      index: item?.meta?.filter?.index || index,
      item_list_name: actionType === 'add' ? getCartItemListName(item) : item?.meta?.filter?.item_list_name,
      price: getPrice(item?.totals?.total) || getPrice(totalPrice),
      quantity: 1,
      upgraded: undefined,
      bundle_id: item.bundle_id || noValue,
      bundle_name: item.bundle_name || noValue,
      attachment_type: item.attachment_type,
      hero_product: getAppVariable('primary_product_type') || undefined,
      fee: item?.totals?.fees || 0,
      tax: item?.totals?.taxes || 0,
      affiliation: noValue,
    };
  }

  if (item.inventory_option?.sub_options?.length) {
    return item.inventory_option.sub_options.map((subOption) => {
      return {
        item_id: item?.inventory_id,
        item_name: item?.item_category === 'extras' ? 'cancellation protection' : item.inventory_item.name,
        coupon: item?.discount && item?.discount?.code ? item.discount.code : null,
        currency: getAppVariable('default_currency'),
        discount: subOption.discount || undefined,
        item_brand: item.bundle_name || noValue,
        item_variant: noValue,
        item_list_id: index,
        item_category: item?.item_category,
        index: item?.meta?.filter?.index || index,
        item_list_name: actionType === 'add' ? getCartItemListName(item) : item?.meta?.filter?.item_list_name,
        price: subOption.totals.single_total,
        quantity: subOption.totals.quantity,
        upgraded: undefined,
        bundle_id: item.bundle_id || noValue,
        bundle_name: item.bundle_name || noValue,
        attachment_type: item.attachment_type,
        hero_product: getAppVariable('primary_product_type') || undefined,
        guest_type: subOption.details.name,
      }
    })
  }

  return {
    item_id: item?.inventory_id,
    item_name: item?.item_category === 'extras' ? 'cancellation protection' : item.inventory_item.name,
    coupon: item?.discount && item?.discount?.code ? item.discount.code : null,
    currency: getAppVariable('default_currency'),
    discount: item?.totals?.discount,
    item_brand: item.bundle_name || noValue,
    item_variant: noValue,
    item_list_id: index,
    item_category: item?.item_category,
    index: item?.meta?.filter?.index || index,
    item_list_name: actionType === 'add' ? getCartItemListName(item) : item?.meta?.filter?.item_list_name,
    price: item?.item_category === 'extras' && item?.cancellation_protection
      ? getPrice(item?.cancellation_protection?.totals?.total)
      : getPrice(item?.totals?.total),
    quantity: 1,
    upgraded: item?.upgraded,
    bundle_id: item.bundle_id || noValue,
    bundle_name: item.bundle_name || noValue,
    attachment_type: item.attachment_type,
    hero_product: getAppVariable('primary_product_type') || undefined,
  }
}).flat();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function pushToGTM(gtm: GTM, gtmData: { [key: string]: any }) {
  if (gtm && gtmData) {
    gtm.push(gtmData);
  }
}

export function refreshDataLayer(gtm: GTM) {
  pushToGTM(gtm, { ecommerce: null });
}

export function trackError(gtm: GTM, errorMessage: string | TranslateResult) {
  pushToGTM(gtm, {
    event: 'error',
    message: errorMessage,
  });
}

export function pushIdentityCallback(gtm: GTM) {
  pushToGTM(gtm, {
    // @ts-expect-error - code provided to integrate mParticle
    identityCallback: function (result) {
      result.getUser().setUserAttributes(result.getPreviousUser().getAllUserAttributes());
      const previousUser = result.getPreviousUser();
      if (previousUser && Object.keys(previousUser.getUserIdentities().userIdentities).length === 0) {
        // @ts-expect-error - code provided to integrate mParticle
        const aliasRequest = mParticle.Identity.createAliasRequest(result.getPreviousUser(), result.getUser());
        // @ts-expect-error - code provided to integrate mParticle
        mParticle.Identity.aliasUsers(aliasRequest);
      }
    }
  });
}

export async function trackUserIdentity(gtm: GTM, email: string, customerUUID?: string): Promise<void> {
  let hashedEmail = '';
  try {
    await createHash(email).then((hash: string) => {
      hashedEmail = hash;
    });
  } catch (error) {
    datadogRum.addError('Error hashing email', {
      error,
      hashedEmail,
      event: 'mp_idsync',
    });
  }
  pushToGTM(gtm, {
    event: 'mp_idsync',
    userIdentities: {
      email,
      other: hashedEmail,
      ...(customerUUID && {customer_id: customerUUID})
    }
  });
}
export function mapPurchaseItems(items: GenerateProduct[], bookingFee: number = 0) {
  const checkoutItems = items.map((item, index) => {
    const productCode = item.product_code;

    if (productCode === EXTRAS_PRODUCT_CODES.TRAVEL_INSURANCE || productCode === 'travel_insurance') {
      return {
        item_id: item.inventory_id,
        item_name: 'Travel Insurance',
        coupon: item.discount && item.discount.code ? item.discount.code : undefined,
        currency: getAppVariable('default_currency'),
        discount: item.totals?.discount || 0,
        item_brand: item.inventory_provider || undefined,
        item_list_position: index,
        index: index,
        item_category: 'Insurance',
        attachment_type: item.attachment_type,
        item_list_name: getCartItemListName(item),
        price: getPrice(item.totals.total),
        quantity: 1,
        bundle_id: item.bundle_id || undefined,
        bundle_name: item.bundle_name || undefined,
        hero_product: getAppVariable('primary_product_type') || undefined,
        fee: item.totals?.fees || 0,
        tax: item.totals?.taxes || 0,
        affiliation: undefined,
      };
    }

    if ((productCode === EXTRAS_PRODUCT_CODES.LOUNGES || productCode === EXTRAS_PRODUCT_CODES.FASTTRACK)
      && item.inventory_option.sub_options.length) {
      return item.inventory_option.sub_options.map((subOption) => {
        return {
          item_id: item.inventory_id,
          item_name: item.inventory_item.name,
          coupon: item.discount && item.discount.code ? item.discount.code : undefined,
          currency: getAppVariable('default_currency'),
          discount: subOption.discount || undefined,
          item_brand: item.bundle_name || undefined,
          item_list_position: index,
          index: index,
          item_category: item.product_code,
          attachment_type: item.attachment_type,
          item_list_name: getCartItemListName(item),
          price: subOption.totals.single_total,
          quantity: subOption.totals.quantity,
          bundle_id: item.bundle_id || undefined,
          bundle_name: item.bundle_name || undefined,
          hero_product: getAppVariable('primary_product_type') || undefined,
          guest_type: subOption.details.name,
        }
      });
    }

    return {
      item_id: item.inventory_id,
      item_name: item.inventory_item.name,
      coupon: item.discount && item.discount.code ? item.discount.code : undefined,
      currency: getAppVariable('default_currency'),
      discount: item.totals.discount,
      affiliation: undefined,
      item_brand: item.bundle_name || undefined,
      item_list_position: index,
      index: index,
      item_category: item.isCancellationProtection ? 'extras' : item.product_code,
      attachment_type: item.attachment_type,
      item_list_name: getCartItemListName(item),
      price: getPrice(item.totals.total),
      quantity: 1,
      bundle_id: item.bundle_id || undefined,
      bundle_name: item.bundle_name || undefined,
      hero_product: getAppVariable('primary_product_type') || undefined,
    }
  }).flat();

  const bookingFeeItem = {
    coupon: checkoutItems[0]?.coupon,
    currency: getAppVariable('default_currency'),
    discount: checkoutItems[0]?.discount,
    item_category: "channel fee",
    item_id: checkoutItems[0]?.item_id,
    item_list_name: "bookingFee",
    item_list_position: checkoutItems.length,
    item_name: "Booking Fee",
    price: bookingFee,
    quantity: 1,
  }

  return [...checkoutItems, bookingFeeItem];
}

export function trackBeginCheckout(gtm: GTM, items: GenerateProduct[], bookingFee: number = 0): void {
  const purchaseItems = mapPurchaseItems(items, bookingFee);

  refreshDataLayer(gtm);
  pushToGTM(gtm, {
    event: 'begin_checkout',
    ecommerce: {
      items: purchaseItems,
    },
  });
}

export function trackSearchItemsImpression(
  gtm: GTM,
  searchResults: SearchProducts[],
  isTaxInclusive = false,
  isFeeInclusive = false,
  itemListName = 'search_results',
) {
  const params = new URLSearchParams(window.location.search);
  const paramDiscountCode = params.get('discount') || undefined;

  const searchImpressions = searchResults.map((item, index) => ({
    item_id: item.productHeader.inventory_id,
    item_name: item.name,
    coupon: item?.discount?.code ? item.discount.code : paramDiscountCode,
    currency: getAppVariable('default_currency'),
    discount: item.productOptions[0].totals.discount || 0,
    index,
    item_category: item.productHeader?.product_code || 'parking',
    item_list_name: itemListName,
    price: getPrice(item.productOptions[0].totals.total),
    salesTaxIncluded: item.productOptions[0].totals.taxes !== 0 ? isTaxInclusive : false,
    feesIncluded: item.productOptions[0].totals.fees !== 0 ? isFeeInclusive : false,
    fees: item.productOptions[0].totals.fees,
    tax: item.productOptions[0].totals.taxes,
    quantity: 1,
  }));

  refreshDataLayer(gtm);
  pushToGTM(gtm, {
    event: 'view_item_list',
    ecommerce: {
      items: searchImpressions,
    },
  });
}

export function trackTop3SearchResults({
  gtm,
  searchResults,
  currentPage,
}: {
  gtm: GTM,
  searchResults: SearchProducts[],
  currentPage: string,
}) {
  pushToGTM(gtm, {
    event: 'top_3_search_results',
    products: [
      ...searchResults.slice(0, 3).map((item: SearchProducts) => ({
        current_page: currentPage,
        product_name: item.name,
        price: getPrice(
          item.productOptions?.[0].totals.channel_based?.total
          ?? item.productOptions?.[0].totals.total
        ),
        product_image: item.product_logo?.src ?? item.images?.[0]?.src,
      }))
    ]
  });
}


function getTimeslotImpressionItem(item: object, index: number, itemListName: string): object {
  const params = new URLSearchParams(window.location.search);
  const paramDiscountCode = params.get('discount') || '';

  return {
    item_id: item.productHeader.inventory_id,
    item_name: item.name,
    coupon: item?.discount?.code ? item.discount.code : paramDiscountCode,
    currency: getAppVariable('default_currency'),
    discount: item.productOptions[0].totals.discount || 0,
    item_category: item.productHeader?.product_code || 'parking',
    item_list_name: itemListName,
    index,
    price: getPrice(item.productOptions[0].totals.total),
    quantity: item.quantity ?? 1,
    affiliation: null,
    item_brand: null,
    item_category2: null,
    item_list_id: null,
    item_variant: null,
    location_id: null
  };
}

export function trackSearchItemsImpressionForTimeslots(
  gtm: GTM,
  searchResults: SearchProducts[],
  itemListName = 'timeslot_search_results',
) {

  const searchImpressions = searchResults.map((item, index) => (
    getTimeslotImpressionItem(item, index, itemListName)
  ));

  refreshDataLayer(gtm);
  pushToGTM(gtm, {
    event: 'view_item_list',
    ecommerce: {
      items: searchImpressions,
    },
  });
}

export function gaEventBookTimeslot(
  gtm: GTM,
  cartItems: SearchProducts[],
  currency: string,
  total: number
) {

  const timeslots = cartItems.map((item, index) => getTimeslotImpressionItem(item, index, 'timeslot selected'));

  pushToGTM(gtm, {
    event: 'view_cart',
    ecommerce: {
      currency: currency || null,
      value: total,
      items: timeslots,
    },
  });
}

interface PurchaseImpressionOptions {
  gtm: GTM;
  items: PurchaseProduct[];
  orderRef: string;
  totals: Cost;
  bookingFee: number;
  itemIndex: number;
  itemListName: string;
  event_name: string;
  bundleName: string;
}

/**
 * Pushes a 'purchase' event to GTM.
 * @param gtm - The Data layer instance
 * @param items - The products
 * @param orderRef - The order ID
 * @param totals - Totals object of the order
 * @param bookingFee - Booking fee amount
 */
export function trackPurchaseImpression({
  gtm,
  items,
  orderRef,
  totals,
  bookingFee = 0,
  itemIndex = 0,
  itemListName = 'search_results',
  event_name = 'purchase',
  bundleName = '',
} : PurchaseImpressionOptions ): void {
  if (!Array.isArray(items)) {
    throw new ReferenceError('Items must be an array');
  }

  const products = items.map(item => {
    const productCode = item.product_code;

    if (productCode === EXTRAS_PRODUCT_CODES.TRAVEL_INSURANCE || productCode === 'travel_insurance') {
      //gets provider from order_criteria for PurchaseProduct
      const provider = item.inventory_provider ||
                      item.order_criteria?.travel_insurance?.inventory_provider ||
                      item.inventory_item?.provider ||
                      bundleName;

      return {
        item_id: item.orderId,
        item_name: 'Travel Insurance',
        coupon: item?.discount?.code ? item.discount.code : undefined,
        affiliation: undefined,
        currency: item.payment.currency.toUpperCase(),
        discount: item.totals?.discount || 0,
        item_brand: provider || undefined,
        item_list_position: itemIndex,
        item_category: 'Insurance',
        item_list_name: itemListName || getCartItemListName(item),
        tax: item.totals.taxes,
        fee: item.totals.fees,
        price: item.totals.total,
        quantity: 1,
        bundle_id: item.bundle_id || undefined,
        bundle_name: bundleName || undefined,
        attachment_type: item.attachment_type,
        hero_product: getAppVariable('primary_product_type') || undefined,
      };
    }

    if ((productCode === EXTRAS_PRODUCT_CODES.LOUNGES || productCode === EXTRAS_PRODUCT_CODES.FASTTRACK)
      && item.inventory_option.sub_options.length) {
      return item.inventory_option.sub_options.map((subOption) => ({
        item_id: item.orderId,
        item_name: item.parkingName,
        coupon: item?.discount?.code ? item.discount.code : undefined,
        affiliation: undefined,
        currency: item.payment.currency.toUpperCase(),
        discount: subOption.discount || undefined,
        item_brand: bundleName || undefined,
        item_list_position: itemIndex,
        item_category: item.product_code,
        item_list_name: itemListName || getCartItemListName(item),
        tax: item.totals.taxes,
        fee: item.totals.fees,
        price: subOption.totals.single_total,
        quantity: subOption.totals.quantity,
        bundle_id: item.bundle_id || undefined,
        bundle_name: bundleName || undefined,
        attachment_type: item.attachment_type,
        hero_product: getAppVariable('primary_product_type') || undefined,
        guest_type: subOption.details.name,
      }));
    }

    return {
      item_id: item.orderId,
      item_name: item.parkingName,
      coupon: item?.discount?.code ? item.discount.code : undefined,
      affiliation: undefined,
      currency: item.payment.currency.toUpperCase(),
      discount: item.totals.discount || 0,
      item_brand: bundleName || undefined,
      item_list_position: itemIndex,
      item_category: item.product_code,
      item_list_name: itemListName || getCartItemListName(item),
      price: item.totals.total,
      tax: item.totals.taxes,
      fee: item.totals.fees,
      quantity: 1,
      bundle_id: item.bundle_id || undefined,
      bundle_name: bundleName || undefined,
      attachment_type: item.attachment_type,
      hero_product: getAppVariable('primary_product_type') || undefined,
    }
  }).flat();

  items.forEach((item) => {
    if (item.cancellation_protection) {
      const itemTaxesSummary = item.cancellation_protection?.taxes
          .reduce((acum: number, curValue: { amount: number }) => acum + curValue.amount, 0);

      products.push({
        item_id: item.cancellation_protection.id,
        item_name: 'cancellation protection',
        coupon: item?.discount?.code ? item.discount.code : '',
        currency: item.payment.currency,
        discount: item?.totals?.discount || 0,
        item_list_position: itemIndex,
        item_category: 'extras',
        item_list_name: itemListName,
        price: item.cancellation_protection.totals.total,
        tax: itemTaxesSummary,
        fee: 0,
        quantity: 1,
      });
    }
  });

  const hasCancellation = items.some(item => item.cancellation_protection?.id);
  const orderTaxesSummary = products.reduce((acum: number, curValue: { tax: number }) => acum + curValue.tax, 0);
  const orderFeesSummary = products.reduce((acum: number, curValue: { fee: number }) => acum + curValue.fee, 0);

  // Adjusted value reporting based on CAVU owned Channels
  const cavuOwnedValues = {
    value: totals.cavu_commission,
    basketValue: totals.total,
  }

  if (getFeatureFlag(FEATURE_FLAGS.ECOM_3346_SHOW_CAVU_COMMISSION_SEPARATELY_FROM_CAVU_TAX, false)) {
    cavuOwnedValues.valueTax = totals.cavu_tax;
  }

  refreshDataLayer(gtm);

  pushToGTM(gtm, {
    event: event_name,
    ecommerce: {
      bookingFee,
      transaction_id: orderRef,
      affiliation: '',
      ...(totals.cavu_commission ? cavuOwnedValues : { value: totals.total }),
      tax: orderTaxesSummary,
      fee: orderFeesSummary,
      currency: products[0].currency,
      coupon: '',
      addedExtra: hasCancellation ? 'cancellation protection' : null,
      paymentType: getPaymentType(items),
      items: products,
    },
  });
}

export function trackAddToCart(gtm: GTM, items: GenerateProduct[]): void {
  const cartItems = convertCartItems(items, 'add');

  refreshDataLayer(gtm);
  pushToGTM(gtm, {
    event: 'add_to_cart',
    ecommerce: {
      items: cartItems,
    },
  });
}

export function trackRemoveFromCart(gtm: GTM, items: GenerateProduct[]): void {
  const cartItems = convertCartItems(items, 'remove');

  pushToGTM(gtm, {
    event: 'remove_from_cart',
    ecommerce: {
      items: cartItems,
    },
  });
}

export function trackViewItem(gtm: GTM, item: SearchProducts, index: number, itemListName: string): void {
  const params = new URLSearchParams(window.location.search);
  const paramDiscountCode = params.get('discount') || undefined;

  const viewItem = {
    item_id: item.productHeader.inventory_id,
    item_name: item.name,
    coupon: paramDiscountCode,
    currency: getAppVariable('default_currency'),
    discount: item.productOptions[0].totals.discount || 0,
    item_list_position: index,
    item_category: 'parking',
    item_list_name: itemListName,
    price: getPrice(item.productOptions[0].totals.total),
    quantity: 1,
  };

  refreshDataLayer(gtm);
  pushToGTM(gtm, {
    event: 'view_item',
    ecommerce: {
      items: [viewItem],
    },
  });
}

export function trackStandaloneInsuranceViewItem(
  gtm: GTM,
  product: SearchProducts,
  insuranceFormData: TravelInsuranceForm
): void {
  const fullGlobalParams = buildInsuranceGaDataLayer(product, insuranceFormData);

  pushToGTM(gtm, fullGlobalParams);

  gaEventLounges(gtm, {
    event: 'view_item',
    items: [{
      ...product,
      item_category: 'Insurance',
    }],
    attachment_type: ATTACHMENT_TYPE.PRIMARY,
  });
}

export function trackStandaloneInsuranceAddToCart(
  gtm: GTM,
  items: GenerateProduct[],
  insuranceFormData: TravelInsuranceForm
): void {
  const cartItems = convertCartItems(items, 'add');

  const insuranceItem = items.find(item =>
    item.product_code === EXTRAS_PRODUCT_CODES.TRAVEL_INSURANCE ||
    item.product_code === 'travel_insurance'
  );

  const fullGlobalParams = buildInsuranceGaDataLayer(insuranceItem, insuranceFormData);

  refreshDataLayer(gtm);

  pushToGTM(gtm, fullGlobalParams);

  pushToGTM(gtm, {
    event: 'add_to_cart',
    ecommerce: {
      items: cartItems,
    },
  });
}

function getDiscount(item: CartItem | SearchProducts | PurchaseProduct): number {
  if(Object.values(item).includes("totals")){
    return item.totals.discount;
  }
  if (Object.values(item).includes("productOptions")) {
    return item.productOptions[0].totals.discount
  }
  return 0;
}

/**
 * Tracks add_to_cart event specifically for travel insurance
 * Includes both ecommerce items and insurance-specific global parameters
 * @param gtm - GTM instance
 * @param items - Cart items for insurance product
 * @param insuranceFormData - Form data from travel insurance store
 * @param searchCriteria - Search box form data for common GTM data
 * @param primaryProductType - Primary product
 */
export function trackInsuranceAddToCart(
  gtm: GTM,
  items: GenerateProduct[],
  insuranceFormData: TravelInsuranceForm,
  searchCriteria: LoungeFormData,
  primaryProductType: string
): void {
  const cartItems = convertCartItems(items, 'add');

  //finding insurance item to extract sub_options  - needed for InsuranceAddOn
  const insuranceItem = items.find(item =>
    item.product_code === EXTRAS_PRODUCT_CODES.TRAVEL_INSURANCE ||
    item.product_code === 'travel_insurance'
  );

  //builds insurance global parameters
  const insuranceGlobalParams = buildInsuranceGtmData(insuranceItem, insuranceFormData);

  const commonGtmData = gaDataLayerCommon(searchCriteria, {
    ...(searchCriteria.airport && { airportCode: searchCriteria.airport }),
  });

  const primaryProductGtmData = getProductGaData(primaryProductType, searchCriteria);

  const fullGlobalParams = {
    ...commonGtmData,
    ...primaryProductGtmData,
    ...insuranceGlobalParams,
  };

  refreshDataLayer(gtm);

  pushToGTM(gtm, gaDataLayer(fullGlobalParams));

  pushToGTM(gtm, {
    event: 'add_to_cart',
    ecommerce: {
      items: cartItems,
    },
  });
}

/**
 *
 * @param gtm - The Data layer instance
 * @param event - event name
 * @param items - The products
 * @param refresh - Clear the previous ecommerce object.
 * @param params - extend a sending ecommerce object
 */
export function gaEventLounges(
  gtm: GTM,
  { event, items = [], params = {}, attachment_type = 'primary' }:
  {
    event: string,
    items: ((CartItem | SearchProducts | PurchaseProduct) & { index?: number })[],
    refresh?: boolean,
    params?: object,
    attachment_type?: (typeof ATTACHMENT_TYPE)[keyof typeof ATTACHMENT_TYPE];
  },
): void {
  const queryParams = new URLSearchParams(window.location.search);
  const paramDiscountCode = queryParams.get('discount') || undefined;

  pushToGTM(gtm, {
    event,
    ecommerce: {
      items: items.map((item, index: number) => {
        const defaultParams = {
          item_id: '',
          item_name: '',
          affiliation: undefined,
          coupon: paramDiscountCode,
          currency: getAppVariable('default_currency'),
          discount: 0,
          index: item?.index || index,
          item_brand: undefined,
          item_list_position: item?.index || index,
          item_category: getAppVariable('primary_product_type'),
          item_list_name: getCartItemListName({ attachment_type }),
          price: 0,
          quantity: 1,
        };
        if ('inventory_id' in item) {
          Object.assign(defaultParams, {
            item_id: item.inventory_id,
            item_name: item.inventory_item?.name,
            price: getPrice(item?.totals?.total),
            quantity: 1,
          });
        } else if ('productHeader' in item) {
          Object.assign(defaultParams, {
            item_id: item?.productHeader?.inventory_id,
            item_name: item.name,
            price: getPrice(item?.productOptions?.[0]?.totals?.total),
            quantity: 1,
            product_code: item?.productHeader?.product_code
          });
        } else {
          Object.assign(defaultParams, item);
        }

        Object.keys(item).forEach((key) => {
          if (Object.hasOwnProperty.call(defaultParams, key)) {
            Object.assign(defaultParams, { [key]: item[key] });
          }
        });

        if(item?.discount && typeof item.discount === 'object' && item?.discount?.code) {
            defaultParams.coupon = item.discount.code
            defaultParams.discount = item.discount.amount
        } else {
          defaultParams.discount = getDiscount(item);
        }

        return { ...defaultParams };
      }),
      ...params,
    },
  });
}

export function trackPurchaseImpressionLounges(gtm: GTM, items: PurchaseProduct[], orderRef: string, event = 'add_payment_info'): void {
  let taxesTotal = 0;
  let price = 0;
  items.forEach((item) => {
    const taxes = item?.taxes || [];
    price += item.totals.total;
    taxes.forEach(({ amount }: { amount: number }) => { taxesTotal += amount; });
  });
  gaEventLounges(gtm, {
    event,
    items,
    params: {
      paymentType: getPaymentType(items),
      currency: items[0]?.payment?.currency,
      coupon: '',
      value: price,
      tax: taxesTotal,
      transaction_id: orderRef,
    },
  });
}

export function trackMarketingChannelImpression({
  gtm,
  checkbox_id,
  checkbox_name,
  checkbox_state,
}: {
  gtm: GTM;
  checkbox_id: string;
  checkbox_name: string;
  checkbox_state: string;
}) {
  pushToGTM(gtm, {
    event: 'checkbox_interaction',
    checkbox_id,
    checkbox_name,
    checkbox_state,
  });
}

export function trackSearchInitiated(gtm: GTM) {
  refreshDataLayer(gtm);
  pushToGTM(gtm, {
    event: 'search_initiated',
  });
}

export function trackUpsellImpression(gtm: GTM, product: Partial<SearchProducts>): void {
  refreshDataLayer(gtm);
  pushToGTM(gtm, {
    event: 'view_item',
    ecommerce: {
      items: [{
        item_id: product.id,
        item_name: product.product_option?.name,
        item_category: 'travel insurance upsell',
        item_list_name: 'Confirmation Page Upsell',
        quantity: 1,
      }],
    },
  });
}

export function trackUpsellClick(gtm: GTM, product: Partial<SearchProducts>, partnerId: number): void {
  pushToGTM(gtm, {
    event: 'select_promotion',
    ecommerce: {
      promotion_id: partnerId.toString(),
      promotion_name: 'Travel Insurance',
      items: [{
        item_id: product.id,
        item_name: product.product_option?.name,
        item_category: 'travel insurance upsell',
      }],
    },
  });
}
